import { Linkedin, Mail, Instagram, ArrowUpRight } from "lucide-react";
import XIcon from "@/components/icons/XIcon";

const LOGO_URL = "/zevnox-logo-white.png";

const NAV_LINKS = [
  { label: "About",       href: "#about" },
  { label: "Why Us",      href: "#why-us" },
  { label: "Industries",  href: "#industries" },
  { label: "Process",     href: "#process" },
  { label: "Insights",    href: "#insights" },
  { label: "Contact",     href: "#contact" },
];

const DESKS = [
  { city: "London",    coords: "51.50°N · 0.12°W"  },
  { city: "Dubai",     coords: "25.20°N · 55.27°E" },
  { city: "Singapore", coords: "1.35°N · 103.81°E" },
  { city: "New York",  coords: "40.71°N · 74.00°W" },
];

const REGIONS = ["EMEA", "APAC", "Americas", "MENA"];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative border-t border-white/8 bg-[#0B0B0B]"
    >
      {/* Top accent divider — premium luminous line */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent 0%, rgba(217,249,157,0.45) 50%, transparent 100%)",
        }}
      />

      {/* Trust positioning band */}
      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div
            data-testid="footer-positioning"
            className="font-mono text-[11px] uppercase tracking-[0.28em] text-neutral-300"
          >
            <span className="text-[#D9F99D]">●</span> Executive Search · Trading
            · FinTech · AI · Digital Assets
          </div>
          <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.24em] text-neutral-500">
            {REGIONS.map((r, i) => (
              <span key={r} className="flex items-center gap-3">
                <span>{r}</span>
                {i < REGIONS.length - 1 && (
                  <span className="text-neutral-700" aria-hidden="true">·</span>
                )}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-10">
          {/* Brand column */}
          <div className="md:col-span-5">
            <img
              src={LOGO_URL}
              alt="Zevnox Search"
              loading="lazy"
              decoding="async"
              width="160"
              height="40"
              className="h-10 w-auto"
              style={{
                filter:
                  "drop-shadow(0 0 10px rgba(255,255,255,0.15)) drop-shadow(0 0 22px rgba(217,249,157,0.18))",
              }}
            />
            <p className="mt-6 max-w-sm text-sm text-neutral-300 leading-relaxed">
              Executive Search for Trading, FinTech &amp; AI. Building
              high‑performance teams across the world&apos;s fastest‑moving
              capital markets.
            </p>

            {/* Direct contact email — premium minimal treatment */}
            <a
              href="mailto:mayur@zevnoxsearch.com"
              data-testid="footer-email"
              className="mt-7 flex w-fit items-center gap-2.5 group"
            >
              <span className="h-8 w-8 rounded-full border border-[#D9F99D]/25 bg-[#D9F99D]/[0.04] flex items-center justify-center text-[#D9F99D] group-hover:border-[#D9F99D]/60 group-hover:bg-[#D9F99D]/[0.08] transition-colors">
                <Mail size={13} aria-hidden="true" />
              </span>
              <span className="flex flex-col">
                <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-neutral-500">
                  Direct
                </span>
                <span className="text-sm text-white group-hover:text-[#D9F99D] transition-colors">
                  mayur@zevnoxsearch.com
                </span>
              </span>
            </a>

            <a
              href="#contact"
              data-testid="footer-cta"
              className="mt-6 flex w-fit items-center gap-2 text-sm font-medium text-white border-b border-[#D9F99D]/40 hover:border-[#D9F99D] hover:text-[#D9F99D] pb-1.5 transition-colors"
            >
              Start an executive mandate
              <ArrowUpRight size={14} aria-hidden="true" />
            </a>
            <div className="mt-8 flex items-center gap-3">
              {[
                { Icon: Linkedin,  label: "LinkedIn",                href: "https://www.linkedin.com/company/zevnox-search/about/" },
                { Icon: XIcon,     label: "X (formerly Twitter)",    href: "https://x.com/ZevnoxSearch" },
                { Icon: Instagram, label: "Instagram",               href: "https://www.instagram.com/zevnoxsearch" },
                { Icon: Mail,      label: "Email Zevnox Search",     href: "mailto:mayur@zevnoxsearch.com" },
              ].map(({ Icon, label, href }, i) => (
                <a
                  key={label}
                  href={href}
                  target={href.startsWith('http') ? '_blank' : undefined}
                  rel="noopener noreferrer"
                  data-testid={`footer-social-${i}`}
                  className="h-9 w-9 rounded-full border border-white/10 bg-white/[0.02] flex items-center justify-center text-neutral-300 hover:text-white hover:border-[#D9F99D]/40 hover:bg-[#D9F99D]/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D9F99D]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0B] transition-colors"
                  aria-label={label}
                >
                  <Icon size={15} aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>

          {/* Navigate */}
          <div className="md:col-span-3">
            <h4 className="text-[11px] font-mono uppercase tracking-[0.24em] text-[#D9F99D]/90">
              Navigate
            </h4>
            <ul className="mt-5 space-y-3.5 text-sm text-neutral-300">
              {NAV_LINKS.map((l) => (
                <li key={l.label}>
                  <a
                    href={l.href}
                    className="inline-flex items-center gap-2 hover:text-white transition-colors group"
                  >
                    <span className="h-px w-3 bg-white/15 group-hover:w-5 group-hover:bg-[#D9F99D]/70 transition-all duration-300" />
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Global Desks */}
          <div className="md:col-span-4">
            <h4 className="text-[11px] font-mono uppercase tracking-[0.24em] text-[#D9F99D]/90">
              Global Desks
            </h4>
            <ul className="mt-5 space-y-3.5 text-sm text-neutral-300">
              {DESKS.map((d) => (
                <li key={d.city} className="flex items-baseline justify-between gap-4">
                  <span className="text-white/95">{d.city}</span>
                  <span className="font-mono text-[11px] text-neutral-500 tracking-[0.06em]">
                    {d.coords}
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-6 flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.22em] text-neutral-500">
              <span className="h-1.5 w-1.5 rounded-full bg-[#D9F99D] zx-pulse" aria-hidden="true" />
              Global response window
            </div>
          </div>
        </div>

        {/* Bottom bar — with premium luminous divider above */}
        <div className="mt-16 relative">
          {/* Soft ambient glow */}
          <div
            aria-hidden
            className="absolute left-1/2 top-0 -translate-x-1/2 h-4 w-2/3 rounded-full pointer-events-none"
            style={{
              background:
                "radial-gradient(60% 120% at 50% 0%, rgba(217,249,157,0.18), rgba(217,249,157,0) 70%)",
              filter: "blur(6px)",
            }}
          />
          {/* Luminous divider line */}
          <div
            aria-hidden
            className="h-px w-full"
            style={{
              background:
                "linear-gradient(90deg, transparent 0%, rgba(217,249,157,0.45) 50%, transparent 100%)",
            }}
          />
          <div className="pt-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-neutral-500">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span>© 2026 Zevnox Search · Executive Search for Trading, FinTech &amp; AI</span>
              <span className="text-neutral-700" aria-hidden="true">·</span>
              <a
                href="/privacy"
                data-testid="footer-privacy-link"
                className="hover:text-white transition-colors"
              >
                Privacy Policy
              </a>
            </div>
            <div className="font-mono uppercase tracking-[0.22em] text-neutral-600">
              Built for operators · Not templates
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
